import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Paper, Table, TableContainer, TableHead, TableBody,
  TableRow, TableCell, Chip, IconButton, LinearProgress, Alert,
  CircularProgress, Button, Divider, Snackbar, ToggleButton, ToggleButtonGroup, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

const API = process.env.REACT_APP_API_URL;

const VERDICT_LABEL = { c: 'Correct', p: 'Partial', w: 'Wrong' };
const VERDICT_COLOR = { c: 'success', p: 'warning', w: 'error' };
const CATS = ['c', 'p', 'w'];

const VENDOR_LABEL = {
  cisco_ftd: 'Cisco FTD',
  paloalto_panos: 'Palo Alto PAN-OS',
  juniper_srx: 'Juniper SRX',
  fortinet_fortigate: 'Fortinet FortiGate',
  huawei_usg: 'Huawei USG',
  checkpoint_security_gateway: 'Check Point',
};

const SOURCE_LABEL = {
  original_100: 'Original cohort (2026-05-03)',
  stratified_96: 'Stratified extension',
  corpus_topup: 'Corpus top-up',
};

// Landis & Koch (1977). Quoted alongside the value so the reader can place it
// without looking the bands up.
function kappaBand(k) {
  if (k === null || k === undefined) return { label: '—', color: 'default' };
  if (k < 0.0) return { label: 'poor', color: 'error' };
  if (k <= 0.20) return { label: 'slight', color: 'error' };
  if (k <= 0.40) return { label: 'fair', color: 'warning' };
  if (k <= 0.60) return { label: 'moderate', color: 'warning' };
  if (k <= 0.80) return { label: 'substantial', color: 'success' };
  return { label: 'almost perfect', color: 'success' };
}

const fmtK = (k) => (k === null || k === undefined ? '—' : k.toFixed(3));
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const fmtCI = (ci) => (ci ? `${ci.point}%  [${ci.low} – ${ci.high}]  ±${ci.half_width}` : '—');
const fmtCIOnly = (ci) => (ci ? `[${ci.low}–${ci.high}] ±${ci.half_width}pp` : '—');

export default function AnnotationStats() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [splitBy, setSplitBy] = useState('by_cohort');
  const [depositing, setDepositing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/annotation/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!res.ok) throw new Error(`Could not load stats (HTTP ${res.status})`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Packages the 300-finding ground truth for public deposit: the machine-readable
  // artefact, two CSVs and a README. Answers Reviewer 2's Area 10 for the COMNET
  // revision. Pseudonymous by construction -- annotators appear as usernames only,
  // and the file holding their real identities is not part of the bundle.
  const downloadDeposit = async () => {
    setDepositing(true);
    try {
      const res = await fetch(`${API}/annotation/deposit-bundle`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!res.ok) throw new Error(`Could not build the bundle (HTTP ${res.status})`);
      const blob = await res.blob();
      // Keep the filename the server chose; it carries the item count.
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const name = match ? match[1] : 'nscat-mapping-ground-truth.zip';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${name}`);
    } catch (e) {
      setToast(e.message);
    } finally {
      setDepositing(false);
    }
  };

  const copyForPaper = () => {
    if (!data) return;
    const a = data.agreement;
    const lines = [];
    lines.push('ANNOTATION STUDY — COMNET-D-26-05631 revision');
    lines.push(`Queue: ${data.queue.total} findings`);
    const m = data.queue.meta || {};
    if (m.source_counts) {
      lines.push(`Composition: ${Object.entries(m.source_counts).map(([k, v]) => `${k}=${v}`).join(', ')}`);
    }
    lines.push('');
    lines.push('PROGRESS');
    data.annotators.forEach((x) => {
      lines.push(`  ${x.username}: ${x.answered}/${x.total} answered` +
        (x.incomplete ? `, ${x.incomplete} missing suggested control` : '') +
        (x.unsure ? `, ${x.unsure} flagged unsure` : ''));
    });
    if (a) {
      lines.push('');
      lines.push('INTER-ANNOTATOR AGREEMENT (raw verdicts, before adjudication)');
      lines.push(`  Overlapping items:        ${a.n_overlap}`);
      lines.push(`  Exact agreement:          ${a.exact_agreements}/${a.n_overlap} (${a.percent_agreement}%)`);
      lines.push(`  Cohen's kappa:            ${fmtK(a.cohen_kappa)} (${kappaBand(a.cohen_kappa).label})`);
      lines.push(`  Cohen's kappa, linear-wt: ${fmtK(a.cohen_kappa_linear_weighted)} (${kappaBand(a.cohen_kappa_linear_weighted).label})`);
      lines.push('');
      lines.push(`  Confusion matrix (rows = ${a.annotator_a}, cols = ${a.annotator_b})`);
      lines.push('           ' + CATS.map((c) => VERDICT_LABEL[c].padStart(9)).join(''));
      CATS.forEach((r) => {
        lines.push('  ' + VERDICT_LABEL[r].padEnd(9) +
          CATS.map((c) => String(a.confusion_matrix[r][c]).padStart(9)).join(''));
      });
      lines.push('');
      lines.push(`  Disagreements requiring adjudication: ${data.disagreements.length}`);
    }
    const cons = data.consensus;
    if (cons && cons.n) {
      lines.push('');
      lines.push('TOP-1 MAPPING PRECISION AS PUBLISHED (consensus rule)');
      lines.push(`  Verdicts scored:  n=${cons.n} (${cons.c} correct, ${cons.p} partial, ${cons.w} wrong)`);
      lines.push(`  Strict:           ${fmtPct(cons.strict_pct)}`);
      lines.push(`  Lenient:          ${fmtPct(cons.lenient_pct)}`);
      lines.push(`  Rule:             ${cons.rule}`);
    }
    if (data.published?.retrieval) {
      const r = data.published.retrieval;
      lines.push(`  RAG retrieval:    ${r.pct}% (${r.hits} of ${r.findings} findings, ${r.audits} audits)`);
    }
    if (data.published?.top_k) {
      const k = data.published.top_k, ref = data.published.reference;
      lines.push(`  Top-k (1/3/5):    ${k.top_1?.pct} / ${k.top_3?.pct} / ${k.top_5?.pct}  (ceiling ${ref?.ceiling_pct}%, ${ref?.resolved} of ${ref?.n} resolvable)`);
    }
    const pooled = data.pooled;
    if (pooled && pooled.n) {
      lines.push('');
      lines.push('TOP-1 MAPPING PRECISION (all verdicts pooled across annotators)');
      lines.push(`  Verdicts scored:  n=${pooled.n} (${pooled.c} correct, ${pooled.p} partial, ${pooled.w} wrong)`);
      lines.push(`  Strict:           ${fmtPct(pooled.strict_pct)}  95% CI ${fmtCIOnly(pooled.strict_ci)}  ${pooled.strict_ci ? pooled.strict_ci.method : ''}`);
      lines.push(`  Lenient:          ${fmtPct(pooled.lenient_pct)}  95% CI ${fmtCIOnly(pooled.lenient_ci)}  ${pooled.lenient_ci ? pooled.lenient_ci.method : ''}`);
      lines.push('');
      lines.push('RE-MAPPING BURDEN (analyst effort implied by the error rate)');
      lines.push(`  Any correction needed (partial or wrong): ${fmtPct(pooled.remap_burden_pct)}`);
      lines.push(`  Full re-mapping needed (wrong only):      ${fmtPct(pooled.strict_burden_pct)}`);
    }
    lines.push('');
    lines.push('TOP-1 MAPPING PRECISION BY COHORT');
    Object.entries(data.by_cohort).forEach(([k, v]) => {
      lines.push(`  ${(SOURCE_LABEL[k] || k).padEnd(32)} n=${String(v.n).padStart(4)}  strict ${fmtPct(v.strict_pct)} ${fmtCIOnly(v.strict_ci)}  lenient ${fmtPct(v.lenient_pct)}`);
    });
    const em = data.error_modes;
    if (em && em.top_migrations && em.top_migrations.length) {
      lines.push('');
      lines.push(`ERROR MODES — most frequent control-family migrations (n=${em.n_scored} corrections)`);
      em.top_migrations.forEach((t) => {
        const share = em.n_scored ? ((100 * t.count) / em.n_scored).toFixed(1) : '—';
        lines.push(`  ${String(t.from_family).padEnd(6)} -> ${String(t.to_family).padEnd(6)} ${String(t.count).padStart(4)}  (${share}% of corrections)`);
      });
    }
    if (data.error_examples && data.error_examples.length) {
      lines.push('');
      lines.push(`ERROR EXAMPLES: ${data.error_examples.length} non-correct verdicts available in the table below`);
      lines.push('  (rule / finding / control selected / control the expert chose — export from the page)');
    }
    navigator.clipboard.writeText(lines.join('\n'));
    setToast('Copied');
  };

  const splitRows = useMemo(() => {
    if (!data) return [];
    const src = data[splitBy] || {};
    const label = splitBy === 'by_cohort' ? SOURCE_LABEL
      : splitBy === 'by_vendor' ? VENDOR_LABEL : {};
    return Object.entries(src).map(([k, v]) => ({ key: label[k] || k, ...v }));
  }, [data, splitBy]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}><Alert severity="error">{error}</Alert></Box>;
  }

  const a = data.agreement;
  const started = data.annotators.some((x) => x.answered > 0);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/mvp-overview')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Paper 1 Annotation Stats
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button
            variant="outlined"
            startIcon={depositing ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={downloadDeposit}
            disabled={depositing}
          >
            {depositing ? 'Building…' : 'Download deposit bundle'}
          </Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copyForPaper}>
            Copy for Paper
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Two-annotator inter-annotator-agreement study answering reviewers R1-8 (annotation process and
        agreement measurement), R2-2 and R4-1 (two experts independent of the author team). Queue of{' '}
        {data.queue.total} findings, both annotators labelling the same items independently.
      </Typography>

      {!started && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No annotations recorded yet. Progress and agreement appear here as the annotators work.
        </Alert>
      )}

      {/* Published figures: what the paper, highlights and graphical abstract state */}
      {(data.consensus || (data.published && data.published.retrieval)) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderTop: '3px solid #1976d2' }}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>
            As published in the paper, the highlights and the graphical abstract
          </Typography>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {data.consensus && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                    {fmtPct(data.consensus.strict_pct)} / {fmtPct(data.consensus.lenient_pct)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Top-1 mapping precision, strict / lenient
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
                    Under the consensus rule: {data.consensus.c} correct, {data.consensus.p} partial,{' '}
                    {data.consensus.w} wrong of {data.consensus.n}. {data.consensus.rule}
                  </Typography>
                </Paper>
              </Grid>
            )}
            {data.published?.retrieval && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {data.published.retrieval.pct}%
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>RAG retrieval success</Typography>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
                    {data.published.retrieval.hits} of {data.published.retrieval.findings} findings across{' '}
                    {data.published.retrieval.audits} audits, against{' '}
                    {data.published.retrieval.controls} NIST controls. A control was returned, not that it was
                    the right one
                  </Typography>
                </Paper>
              </Grid>
            )}
            {data.published?.top_k && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {data.published.top_k.top_1?.pct} / {data.published.top_k.top_3?.pct} /{' '}
                    {data.published.top_k.top_5?.pct}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Top-k accuracy, k = 1, 3, 5</Typography>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
                    {data.published.reference?.resolved} of {data.published.reference?.n} findings have an
                    agreed correct control, so no top-k figure can exceed{' '}
                    {data.published.reference?.ceiling_pct}%
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Progress */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Progress</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Annotator</strong></TableCell>
                <TableCell sx={{ width: 260 }}><strong>Answered</strong></TableCell>
                <TableCell align="right">Correct</TableCell>
                <TableCell align="right">Partial</TableCell>
                <TableCell align="right">Wrong</TableCell>
                <TableCell align="right"><strong>Strict</strong></TableCell>
                <TableCell align="right"><strong>Lenient</strong></TableCell>
                <TableCell align="right">Unsure</TableCell>
                <TableCell align="right">Incomplete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.annotators.map((x) => (
                <TableRow key={x.username} hover>
                  <TableCell><strong>{x.username}</strong></TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LinearProgress
                        variant="determinate"
                        value={x.total ? (x.answered / x.total) * 100 : 0}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 60 }}>
                        {x.answered}/{x.total}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{x.distribution.c}</TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main' }}>{x.distribution.p}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{x.distribution.w}</TableCell>
                  <TableCell align="right"><strong>{fmtPct(x.distribution.strict_pct)}</strong></TableCell>
                  <TableCell align="right"><strong>{fmtPct(x.distribution.lenient_pct)}</strong></TableCell>
                  <TableCell align="right">{x.unsure || '—'}</TableCell>
                  <TableCell align="right">
                    {x.incomplete ? <Chip size="small" color="warning" label={x.incomplete} /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Agreement */}
      {a ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Inter-annotator agreement — raw verdicts, before adjudication
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {fmtK(a.cohen_kappa)}{' '}
                  <Chip size="small" color={kappaBand(a.cohen_kappa).color} label={kappaBand(a.cohen_kappa).label} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cohen’s κ, unweighted — the conventional figure
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {fmtK(a.cohen_kappa_linear_weighted)}{' '}
                  <Chip size="small" color={kappaBand(a.cohen_kappa_linear_weighted).color} label={kappaBand(a.cohen_kappa_linear_weighted).label} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cohen’s κ, linear-weighted — correct for an ordinal scale, since a
                  Correct/Partial split is less serious than Correct/Wrong
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{a.percent_agreement}%</Typography>
                <Typography variant="body2" color="text.secondary">
                  Raw agreement — {a.exact_agreements} of {a.n_overlap} items identical. Reported
                  alongside κ because κ can read low when one category dominates the marginals.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Confusion matrix — rows {a.annotator_a}, columns {a.annotator_b}
            </Typography>
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {CATS.map((c) => (
                      <TableCell key={c} align="right"><strong>{VERDICT_LABEL[c]}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {CATS.map((r) => (
                    <TableRow key={r}>
                      <TableCell><strong>{VERDICT_LABEL[r]}</strong></TableCell>
                      {CATS.map((c) => (
                        <TableCell
                          key={c}
                          align="right"
                          sx={{
                            fontWeight: r === c ? 700 : 400,
                            bgcolor: r === c ? 'rgba(76,175,80,0.12)' : 'inherit',
                          }}
                        >
                          {a.confusion_matrix[r][c]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Diagonal is agreement. Off-diagonal mass adjacent to the diagonal indicates a rubric
              boundary question; mass in the Correct/Wrong corners indicates a substantive dispute.
            </Typography>
          </Paper>
        </Stack>
      ) : started && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Agreement appears once both annotators have answered overlapping items.
        </Alert>
      )}

      {/* Headline precision with intervals + re-mapping burden */}
      {data.pooled?.n > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Top-1 mapping precision, pooled — n = {data.pooled.n}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {fmtPct(data.pooled.strict_pct)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Strict (Correct only) — 95% CI {fmtCI(data.pooled.strict_ci)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.pooled.strict_ci?.method}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {fmtPct(data.pooled.lenient_pct)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lenient (Correct + ½ Partial) — 95% CI {fmtCI(data.pooled.lenient_ci)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.pooled.lenient_ci?.method} — lenient is a mean of bounded scores, not a
                  binomial proportion, so Wilson does not apply to it
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Manual re-mapping burden
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {fmtPct(data.pooled.remap_burden_pct)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of findings need auditor attention — every verdict other than Correct
                  ({data.pooled.p} Partial + {data.pooled.w} Wrong of {data.pooled.n})
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {fmtPct(data.pooled.strict_burden_pct)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  need a full re-map — the control is in the wrong security domain entirely
                </Typography>
              </Box>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                Answers R1-7, which asks how much manual re-mapping is acceptable and how auditors
                should read these mappings in production.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* Error-mode taxonomy */}
      {data.error_modes?.n_scored > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Error modes — where mappings go wrong (n = {data.error_modes.n_scored})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            NIST family the system assigned versus the family the annotator says applies, over every
            Partial and Wrong verdict. This is the error taxonomy R1-7 asks for — distinct from the
            confusion matrix above, which compares the two annotators to each other.
          </Typography>
          <TableContainer sx={{ maxHeight: 340 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Assigned family</strong></TableCell>
                  <TableCell><strong>Should have been</strong></TableCell>
                  <TableCell align="right"><strong>Count</strong></TableCell>
                  <TableCell align="right">Share of errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.error_modes.top_migrations.map((mg) => (
                  <TableRow key={`${mg.from_family}->${mg.to_family}`} hover>
                    <TableCell>
                      <Chip size="small" label={mg.from_family} color="error" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={mg.to_family} color="success" variant="outlined" />
                    </TableCell>
                    <TableCell align="right"><strong>{mg.count}</strong></TableCell>
                    <TableCell align="right">
                      {((100 * mg.count) / data.error_modes.n_scored).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Breakdowns */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Top-1 precision, pooled across annotators
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={splitBy} onChange={(_, v) => v && setSplitBy(v)}>
            <ToggleButton value="by_cohort">By cohort</ToggleButton>
            <ToggleButton value="by_vendor">By vendor</ToggleButton>
            <ToggleButton value="by_severity">By severity</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>{splitBy === 'by_cohort' ? 'Cohort' : splitBy === 'by_vendor' ? 'Vendor' : 'Severity'}</strong></TableCell>
                <TableCell align="right"><strong>N</strong></TableCell>
                <TableCell align="right">C</TableCell>
                <TableCell align="right">P</TableCell>
                <TableCell align="right">W</TableCell>
                <TableCell align="right"><strong>Strict</strong></TableCell>
                <TableCell align="right">95% CI</TableCell>
                <TableCell align="right"><strong>Lenient</strong></TableCell>
                <TableCell align="right">Re-map burden</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {splitRows.length === 0 && (
                <TableRow><TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">No answers yet.</Typography>
                </TableCell></TableRow>
              )}
              {splitRows.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell>{r.key}</TableCell>
                  <TableCell align="right">{r.n}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{r.c}</TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main' }}>{r.p}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{r.w}</TableCell>
                  <TableCell align="right"><strong>{fmtPct(r.strict_pct)}</strong></TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {r.strict_ci ? `${r.strict_ci.low}–${r.strict_ci.high}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right"><strong>{fmtPct(r.lenient_pct)}</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main' }}>
                    {fmtPct(r.remap_burden_pct)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Strict = Correct only. Lenient = Correct + 0.5 × Partial — the same scoring used for the
          27% / 39% currently in the manuscript, so the figures are directly comparable.
        </Typography>
      </Paper>

      {/* Error examples */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Error examples — {data.error_examples?.length || 0} non-Correct verdict
            {data.error_examples?.length === 1 ? '' : 's'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Every Partial and Wrong verdict with the rule, the finding, the control NSCAT selected and
            the control the annotator identified — the four columns R2-11 asks for. Draw the
            manuscript's error table from here. Wrong verdicts are listed first.
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: '50vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>#</TableCell>
                <TableCell sx={{ width: 90 }}>Verdict</TableCell>
                <TableCell sx={{ width: 100 }}>Annotator</TableCell>
                <TableCell sx={{ width: 180 }}>NSCAT selected</TableCell>
                <TableCell sx={{ width: 130 }}>Expert says</TableCell>
                <TableCell>Rule / Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!data.error_examples || data.error_examples.length === 0) && (
                <TableRow><TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No Partial or Wrong verdicts recorded yet.
                  </Typography>
                </TableCell></TableRow>
              )}
              {(data.error_examples || []).map((e, i) => (
                <TableRow key={`${e.queue_id}-${e.annotator}-${i}`} hover sx={{ verticalAlign: 'top' }}>
                  <TableCell><Typography variant="caption">{e.queue_id}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" color={VERDICT_COLOR[e.verdict]} label={VERDICT_LABEL[e.verdict]}
                      sx={{ height: 20, fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell><Typography variant="caption">{e.annotator}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {e.assigned_nist}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.assigned_nist_details}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main' }}>
                      {e.suggested}
                    </Typography>
                    {e.assigned_family !== e.suggested_family && (
                      <Chip size="small" variant="outlined" label={`${e.assigned_family}→${e.suggested_family}`}
                        sx={{ height: 18, fontSize: '0.6rem', mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                      {e.rule}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{e.issue}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Adjudication queue */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Adjudication queue — {data.disagreements.length} item
            {data.disagreements.length === 1 ? '' : 's'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Verdict disagreements plus anything either annotator flagged unsure. Under the consensus
            rule (Section 7.4) a split verdict resolves to the lower of the two categories; κ is
            computed on the raw verdicts.
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>#</TableCell>
                <TableCell sx={{ width: 90 }}>Reason</TableCell>
                <TableCell sx={{ width: 200 }}>Assigned control</TableCell>
                <TableCell sx={{ width: 190 }}>{a?.annotator_a || 'Annotator A'}</TableCell>
                <TableCell sx={{ width: 190 }}>{a?.annotator_b || 'Annotator B'}</TableCell>
                <TableCell sx={{ width: 110 }}>Vendor</TableCell>
                <TableCell>Rule / Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.disagreements.length === 0 && (
                <TableRow><TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    Nothing to adjudicate yet.
                  </Typography>
                </TableCell></TableRow>
              )}
              {data.disagreements.map((d) => (
                <TableRow key={d.queue_id} hover sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <Typography variant="caption">{d.queue_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={d.reason}
                      color={d.reason === 'unsure' ? 'info' : 'warning'}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {d.assigned_nist}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.assigned_nist_details}
                    </Typography>
                  </TableCell>
                  {['a', 'b'].map((side) => (
                    <TableCell key={side}>
                      <Chip
                        size="small"
                        color={VERDICT_COLOR[d[side].verdict]}
                        label={VERDICT_LABEL[d[side].verdict] || '—'}
                        sx={{ height: 20, fontSize: '0.65rem', mb: 0.5 }}
                      />
                      {d[side].unsure && (
                        <Chip size="small" color="info" label="unsure" sx={{ height: 20, fontSize: '0.6rem', ml: 0.5 }} />
                      )}
                      {d[side].suggested && (
                        <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                          → {d[side].suggested}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Typography variant="caption">{VENDOR_LABEL[d.vendor] || d.vendor}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                      {d.rule}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{d.issue}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
